import type { SxProps, Theme } from "@mui/material";
import { Box } from "@mui/material";
import type { ReactNode, PointerEvent as ReactPointerEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { clampNumber } from "../utilities/numberHelper";

const dividerHitboxWidth = 8;
const dividerVisualWidth = 1;

const clampLeftWidth = (
  requestedLeftWidth: number,
  containerWidth: number,
  minLeftWidth: number,
  minRightWidth: number
): number => {
  const maxLeftWidth = Math.max(0, containerWidth - minRightWidth - dividerHitboxWidth);
  const minClamped = Math.min(minLeftWidth, maxLeftWidth);
  return clampNumber(requestedLeftWidth, minClamped, maxLeftWidth);
};

interface BaseProps {
  leftPane: ReactNode;
  rightPane: ReactNode;
  minLeftWidth: number;
  minRightWidth: number;
  containerSx?: SxProps<Theme>;
  leftPaneSx?: SxProps<Theme>;
  rightPaneSx?: SxProps<Theme>;
  isLeftPaneCollapsed?: boolean;
  isRightPaneCollapsed?: boolean;
}

interface PropsWithInitialLeftWidth extends BaseProps {
  initialLeftWidth: number;
  initialRightWidth?: never;
}

interface PropsWithInitialRightWidth extends BaseProps {
  initialLeftWidth?: never;
  initialRightWidth: number;
}

interface PropsWithEqualPanes extends BaseProps {
  initialLeftWidth?: never;
  initialRightWidth?: never;
}

type Props = PropsWithInitialLeftWidth | PropsWithInitialRightWidth | PropsWithEqualPanes;

const HorizontalSplitPane = ({
  leftPane,
  rightPane,
  minLeftWidth,
  minRightWidth,
  containerSx,
  leftPaneSx,
  rightPaneSx,
  isLeftPaneCollapsed,
  isRightPaneCollapsed,
  initialLeftWidth,
  initialRightWidth,
}: Props) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const leftPaneRef = useRef<HTMLDivElement | null>(null);
  const dragStartXRef = useRef(0);
  const dragStartLeftWidthRef = useRef(0);
  const [isDragging, setIsDragging] = useState(false);

  const explicitInitialLeftWidth =
    initialLeftWidth !== undefined ? Math.max(initialLeftWidth, minLeftWidth) : undefined;

  const explicitInitialRightWidth =
    initialRightWidth !== undefined ? Math.max(initialRightWidth, minRightWidth) : undefined;

  const [leftWidth, setLeftWidth] = useState<number | undefined>(explicitInitialLeftWidth);

  const getRenderedLeftWidth = useCallback((): number => {
    return leftPaneRef.current?.getBoundingClientRect().width ?? minLeftWidth;
  }, [minLeftWidth]);

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>): void => {
    if (event.button !== 0) {
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    dragStartXRef.current = event.clientX;
    dragStartLeftWidthRef.current = getRenderedLeftWidth();
    setIsDragging(true);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>): void => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
      return;
    }

    const containerWidth = containerRef.current?.getBoundingClientRect().width;
    if (containerWidth === undefined) {
      return;
    }

    const requestedWidth = dragStartLeftWidthRef.current + event.clientX - dragStartXRef.current;
    setLeftWidth(clampLeftWidth(requestedWidth, containerWidth, minLeftWidth, minRightWidth));
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>): void => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
      return;
    }

    event.currentTarget.releasePointerCapture(event.pointerId);
    setIsDragging(false);
  };

  const handleLostPointerCapture = (): void => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };
  }, [isDragging]);

  const getLeftPaneFlex = (): string => {
    if (isRightPaneCollapsed) {
      return "1 1 auto";
    }
    if (leftWidth !== undefined) {
      return `0 0 ${leftWidth}px`;
    }
    return explicitInitialRightWidth !== undefined ? "1 1 auto" : "1 1 0";
  };

  const getRightPaneFlex = (): string => {
    if (isRightPaneCollapsed) {
      return "0 0 auto";
    }
    if (leftWidth !== undefined) {
      return "1 1 auto";
    }
    return explicitInitialRightWidth !== undefined ? `0 0 ${explicitInitialRightWidth}px` : "1 1 0";
  };

  return (
    <Box
      ref={containerRef}
      sx={{ position: "relative", display: "flex", height: "100%", width: "100%", overflow: "hidden", ...containerSx }}
    >
      <Box
        ref={leftPaneRef}
        sx={{
          flex: getLeftPaneFlex(),
          maxWidth: isRightPaneCollapsed ? "none" : `calc(100% - ${minRightWidth + dividerHitboxWidth}px)`,
          minWidth: 0,
          overflow: "auto",
          ...leftPaneSx,
          ...(isLeftPaneCollapsed ? { visibility: "hidden" } : {}),
        }}
      >
        {leftPane}
      </Box>
      <Box
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onLostPointerCapture={handleLostPointerCapture}
        sx={{
          position: "relative",
          display: isLeftPaneCollapsed || isRightPaneCollapsed ? "none" : "flex",
          flex: `0 0 ${dividerHitboxWidth}px`,
          alignItems: "stretch",
          justifyContent: "center",
          cursor: "col-resize",
          backgroundColor: isDragging ? "action.selected" : "transparent",
          userSelect: "none",
          touchAction: "none",
          "&:hover": {
            backgroundColor: "action.hover",
          },
          "&::before": {
            content: '""',
            width: `${dividerVisualWidth}px`,
            backgroundColor: "divider",
          },
        }}
      />
      <Box
        sx={{
          flex: getRightPaneFlex(),
          minWidth: 0,
          overflow: "auto",
          ...rightPaneSx,
          ...(isLeftPaneCollapsed ? { position: "absolute", inset: 0 } : {}),
        }}
      >
        {rightPane}
      </Box>
    </Box>
  );
};

export default HorizontalSplitPane;
